import { CaretDown, CaretUp, Plus, Trash } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ThinkerFacet } from "~/api/aiAdminTypes";
import { MAX_THINKER_FACETS, THINKER_PURSUITS } from "~/api/aiAdminTypes";
import type { ThinkerFacetInput } from "~/api/client";
import { useApi } from "~/api/provider";
import { Pill } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import { type BindingKind, FacetSeat } from "~/features/settings/sections/thinkers/FacetSeat";
import { HiveSettings, hiveQueryKey } from "~/features/settings/sections/thinkers/HiveSettings";
import { pursuitLabel } from "~/features/settings/sections/thinkers/labels";
import {
  panelId,
  type ThinkerPanel,
  tabId,
} from "~/features/settings/sections/thinkers/ThinkerTabs";
import { TryIt } from "~/features/settings/sections/thinkers/TryIt";
import { useFacetSeats } from "~/features/settings/sections/thinkers/threads/threadsApi";
import { Button } from "~/foundation/components/Button";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/** One facet as the form holds it: interests as the typed text, plus a key that survives edits. */
interface FacetDraft {
  key: string;
  id: string;
  name: string;
  intent: string;
  interests: string;
  toward: string;
  amplitude: number;
  pursuit: string;
  voice: string;
  /** The facet that decides for the thinker after the hive has voted. At most one. */
  authority: boolean;
  /** BRONCO Opus 03 — who plays its seat, and the room acts it may write. */
  bindingKind: BindingKind;
  bindingValue: string;
  acts: string[];
  /** Carried as loaded (not edited here). */
  sliceChars: number | null;
  enabled: boolean;
}

let nextKey = 0;
const mintKey = () => `facet-${nextKey++}`;

const splitList = (text: string): string[] =>
  text
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

/** A stable colour per facet (golden-angle hues) for the row's initial. */
const facetColor = (index: number) => `hsl(${Math.round((index * 137.508) % 360)} 58% 42%)`;

function toDraft(facet: ThinkerFacet): FacetDraft {
  return {
    key: mintKey(),
    id: facet.id,
    name: facet.name,
    intent: facet.intent,
    interests: facet.interests.join(", "),
    toward: facet.toward ?? "",
    amplitude: facet.amplitude,
    pursuit: facet.pursuit,
    voice: facet.voice ?? "",
    authority: facet.authority === true,
    bindingKind: facet.binding?.role
      ? "role"
      : facet.binding?.capability
        ? "capability"
        : facet.binding?.thinkerId
          ? "thinker"
          : "none",
    bindingValue:
      facet.binding?.role ?? facet.binding?.capability ?? facet.binding?.thinkerId ?? "",
    acts: facet.acts ?? [],
    sliceChars: facet.sliceChars ?? null,
    enabled: facet.enabled !== false,
  };
}

function toInput(draft: FacetDraft): ThinkerFacetInput {
  return {
    id: draft.id,
    name: draft.name.trim(),
    intent: draft.intent.trim(),
    interests: splitList(draft.interests),
    toward: draft.toward.trim() || null,
    amplitude: draft.amplitude,
    pursuit: draft.pursuit,
    voice: draft.voice.trim() || null,
    ...(draft.authority ? { authority: true } : {}),
    ...(draft.bindingKind !== "none" && draft.bindingValue.trim()
      ? {
          binding:
            draft.bindingKind === "role"
              ? { role: draft.bindingValue.trim() }
              : draft.bindingKind === "capability"
                ? { capability: draft.bindingValue.trim() }
                : { thinkerId: draft.bindingValue.trim() },
        }
      : {}),
    ...(draft.acts.length > 0 ? { acts: draft.acts } : {}),
    ...(draft.sliceChars != null ? { sliceChars: draft.sliceChars } : {}),
    ...(draft.enabled ? {} : { enabled: false }),
  };
}

/** A tab panel inside the dialog, or a plain block when embedded without tabs. Module-level on
 *  purpose: a component defined inside the editor would remount its whole subtree on every render. */
function Part({
  panel,
  id: part,
  className,
  children,
}: {
  panel: ThinkerPanel | undefined;
  id: Exclude<ThinkerPanel, "model">;
  className?: string;
  children: ReactNode;
}) {
  return panel === undefined ? (
    <div className={className}>{children}</div>
  ) : (
    <section
      role="tabpanel"
      id={panelId(part)}
      aria-labelledby={tabId(part)}
      hidden={panel !== part}
      className={className}
    >
      {children}
    </section>
  );
}

function blankDraft(): FacetDraft {
  return {
    key: mintKey(),
    id: "",
    name: "",
    intent: "",
    interests: "",
    toward: "",
    amplitude: 1,
    pursuit: "propose",
    voice: "",
    authority: false,
    bindingKind: "none",
    bindingValue: "",
    acts: [],
    sliceChars: null,
    enabled: true,
  };
}

/**
 * Thinker persona — a thinker's **facets**: the roles inside its own thought process, each with
 * an intent and the topics it cares about. Blended by the situation, or — as a HIVE — each
 * answering on its own, voting by weight, with the AUTHORITY deciding (owner, 2026-10-10).
 *
 * The list saves on its own (`PUT …/facets` replaces the whole list), separately from the
 * dialog that hosts it: the dialog's save inherits omitted fields, this one does not — an
 * omitted facet IS a removal — so the two writes must not share a button. The hive policy saves
 * on its own too (`PUT …/hive`, every change at once).
 *
 * Layout (2026-10-10, "once a facet is selected the setup is unusable"): a facet is ONE compact
 * row — initial · name · acts-by · weight · authority · chevron — and only one row is open at a
 * time; the open editor lays its fields in two columns from `md`. Given `panel`, the three parts
 * (facets, hive, try it) render as the dialog's tab panels, all mounted so a draft or a try
 * survives a tab switch; without it they stack (the embedded form).
 */
export function FacetsEditor({
  kind,
  id,
  name,
  panel,
}: {
  kind: "local" | "persona";
  id: string;
  /** The thinker's display name, for the Try-it box ("‹Name› is thinking…"). */
  name?: string;
  /** The dialog's current tab; the parts not shown stay mounted but hidden. */
  panel?: ThinkerPanel;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const store = kind === "local" ? api.thinkers.localFacets : api.thinkers.personaFacets;
  const hiveStore = kind === "local" ? api.thinkers.localHive : api.thinkers.personaHive;
  const queryKey = ["thinkers", "facets", kind, id] as const;

  const query = useQuery({ queryKey, queryFn: () => store.get(id) });
  // BRONCO Opus 03: what each saved facet's binding resolves to right now (or why it cannot play).
  const owner = kind === "local" ? { localId: id } : { personaId: id };
  const seats = useFacetSeats(owner);
  const hive = useQuery({ queryKey: hiveQueryKey(kind, id), queryFn: () => hiveStore.get(id) });
  const loaded = useMemo(() => (query.data ?? []).map(toDraft), [query.data]);
  const [drafts, setDrafts] = useState<FacetDraft[] | null>(null);
  const rows = drafts ?? loaded;
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The ONE facet whose full editor is open. A new facet opens itself. */
  const [open, setOpen] = useState<string | null>(null);
  // The work library (`GET thinkers/facets/library`): 14 facets relevant to work, to start from.
  const library = useQuery({
    queryKey: ["thinkers", "facets", "library"],
    queryFn: () => api.thinkers.facetLibrary(),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const full = rows.length >= MAX_THINKER_FACETS;
  const present = (facet: ThinkerFacet) =>
    rows.some(
      (row) =>
        row.id === facet.id || row.name.trim().toLowerCase() === facet.name.trim().toLowerCase(),
    );
  const offered = (library.data ?? []).filter((facet) => !present(facet));
  const addDraft = (draft: FacetDraft) => {
    setDrafts([...rows, draft]);
    setOpen(draft.key);
  };
  const addFromLibrary = (facetId: string) => {
    const facet = library.data?.find((entry) => entry.id === facetId);
    if (!facet || full || present(facet)) return;
    addDraft(toDraft(facet));
  };

  const dirty =
    drafts !== null && JSON.stringify(drafts.map(toInput)) !== JSON.stringify(loaded.map(toInput));

  const save = useMutation({
    mutationFn: (input: ThinkerFacetInput[]) => store.put(id, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved);
      void queryClient.invalidateQueries({ queryKey: ["thinkers", "facet-seats"] });
      setDrafts(null);
      setSaveError(null);
      toast.success(t("Facets saved"));
    },
    onError: (error: Error) => setSaveError(error.message),
  });

  const update = (key: string, patch: Partial<FacetDraft>) =>
    setDrafts(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  /** Exactly one authority, or none ("No authority" in the header clears it). */
  const setAuthority = (key: string | null) =>
    setDrafts(rows.map((row) => ({ ...row, authority: row.key === key })));

  if (query.isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div
      className={cn("flex flex-col gap-3", panel === undefined && "border-t border-divider pt-3")}
    >
      <Part panel={panel} id="facets" className="flex flex-col gap-3 pt-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <h3 className="font-medium">{t("Facets")}</h3>
            <Text variant="small">
              {t(
                "The roles inside this thinker's own thinking, each with what it wants and the topics it cares about. In a conversation the thinker blends them by the situation.",
              )}
            </Text>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {rows.some((row) => row.authority) ? (
              <Button size="sm" variant="neutral" onClick={() => setAuthority(null)}>
                {t("No authority")}
              </Button>
            ) : null}
            <Button
              size="sm"
              icon={Plus}
              disabled={rows.length >= MAX_THINKER_FACETS}
              onClick={() => addDraft(blankDraft())}
            >
              {t("Add facet")}
            </Button>
          </div>
        </div>

        {query.isError ? (
          <p role="alert" className="text-small text-danger">
            {t("The facets could not be loaded.")}
          </p>
        ) : null}

        {/* Start from the work library: a chosen facet joins the list prefilled and editable. */}
        <Select
          label={t("Add from the work library")}
          value=""
          fullWidth
          disabled={full || library.isLoading || offered.length === 0}
          hint={
            full
              ? t("A thinker has at most {{count}} facets.", { count: MAX_THINKER_FACETS })
              : library.isError
                ? t("The work library could not be loaded.")
                : offered.length === 0 && library.data
                  ? t("Every library facet is already here.")
                  : t("Builder, Tester, Skeptic… each arrives prefilled; edit it as you like.")
          }
          onChange={(event) => addFromLibrary(event.target.value)}
        >
          <option value="">
            {full
              ? t("No room for more facets")
              : library.isLoading
                ? t("Loading…")
                : t("Choose a facet…")}
          </option>
          {offered.map((facet) => (
            <option key={facet.id} value={facet.id}>
              {facet.name} — {facet.intent}
            </option>
          ))}
        </Select>

        {rows.length === 0 ? (
          <Text variant="small">
            {t("No facets yet. Without them the plain system prompt applies.")}
          </Text>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((row, index) => {
              const label = row.name.trim() || t("Facet {{n}}", { n: index + 1 });
              const expanded = open === row.key;
              // Chips only — the wire keeps what was typed; the server counts interests as typed.
              const interests = Array.from(new Set(splitList(row.interests)));
              return (
                <li
                  key={row.key}
                  className={cn(
                    "flex flex-col gap-2 rounded border border-divider p-2",
                    expanded && "border-text/40",
                  )}
                  aria-label={label}
                >
                  {/* The compact row: everything you need to see or set without opening it. */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      aria-hidden
                      style={{ backgroundColor: facetColor(index) }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                    >
                      {(label.trim()[0] ?? "?").toUpperCase()}
                    </span>
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-start"
                      aria-expanded={expanded}
                      aria-controls={`facet-editor-${row.key}`}
                      onClick={() => setOpen(expanded ? null : row.key)}
                    >
                      <span className="block truncate text-small font-medium text-text">
                        {label}
                      </span>
                      {!expanded ? (
                        <span className="block truncate text-small text-text-tertiary">
                          {pursuitLabel(row.pursuit, t)}
                          {row.intent.trim() ? ` · ${row.intent.trim()}` : ""}
                        </span>
                      ) : null}
                    </button>
                    <label className="flex shrink-0 items-center gap-1.5 text-small text-text-secondary">
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={row.amplitude}
                        className="w-20"
                        aria-label={t("Weight of {{name}}", { name: label })}
                        title={t("Weight (vote)")}
                        onChange={(event) =>
                          update(row.key, { amplitude: Number.parseFloat(event.target.value) })
                        }
                      />
                      <span className="w-8 text-end tabular-nums">
                        {Math.round(row.amplitude * 100)}%
                      </span>
                    </label>
                    {/* The AUTHORITY: one radio across the facets; "No authority" in the header clears it. */}
                    <label className="flex shrink-0 items-center gap-1.5 text-small text-text-secondary">
                      <input
                        type="radio"
                        name={`authority-${kind}-${id}`}
                        checked={row.authority}
                        aria-label={t("Authority: {{name}}", { name: label })}
                        onChange={() => setAuthority(row.key)}
                      />
                      <span className="hidden md:inline">{t("Decides for the thinker")}</span>
                    </label>
                    <IconButton
                      icon={expanded ? CaretUp : CaretDown}
                      aria-label={
                        expanded
                          ? t("Collapse {{name}}", { name: label })
                          : t("Edit {{name}}", { name: label })
                      }
                      size="sm"
                      onClick={() => setOpen(expanded ? null : row.key)}
                    />
                    <IconButton
                      icon={Trash}
                      aria-label={t("Remove {{name}}", { name: label })}
                      size="sm"
                      onClick={() => setDrafts(rows.filter((entry) => entry.key !== row.key))}
                    />
                  </div>

                  {expanded ? (
                    <div id={`facet-editor-${row.key}`} className="grid gap-3 md:grid-cols-2">
                      <Input
                        label={t("Name")}
                        value={row.name}
                        fullWidth
                        placeholder={t("e.g. Builder")}
                        onChange={(event) => update(row.key, { name: event.target.value })}
                      />
                      <Select
                        label={t("Acts by")}
                        value={row.pursuit}
                        fullWidth
                        onChange={(event) => update(row.key, { pursuit: event.target.value })}
                      >
                        {THINKER_PURSUITS.map((pursuit) => (
                          <option key={pursuit} value={pursuit}>
                            {pursuitLabel(pursuit, t)}
                          </option>
                        ))}
                      </Select>
                      <div className="md:col-span-2">
                        <Input
                          label={t("Intent")}
                          value={row.intent}
                          fullWidth
                          placeholder={t("e.g. Make it shippable")}
                          hint={t("What this facet wants in any exchange.")}
                          onChange={(event) => update(row.key, { intent: event.target.value })}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5 md:col-span-2">
                        <Input
                          label={t("Interests")}
                          value={row.interests}
                          fullWidth
                          placeholder={t("e.g. latency, cost, player feel")}
                          hint={t("Comma-separated. The topics it attends to first, up to 12.")}
                          onChange={(event) => update(row.key, { interests: event.target.value })}
                        />
                        {interests.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {interests.map((interest) => (
                              <Pill key={interest}>{interest}</Pill>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <Input
                        label={t("Toward")}
                        value={row.toward}
                        fullWidth
                        placeholder={t("The outcome it pushes for")}
                        onChange={(event) => update(row.key, { toward: event.target.value })}
                      />
                      <Input
                        label={t("Voice")}
                        value={row.voice}
                        fullWidth
                        placeholder={t("One sentence of tone")}
                        onChange={(event) => update(row.key, { voice: event.target.value })}
                      />
                      <Text variant="small" className="md:col-span-2">
                        {t(
                          "How loudly it speaks when it is relevant; in a hive, how much its vote counts.",
                        )}
                      </Text>
                      <FacetSeat
                        owner={owner}
                        facetId={row.id}
                        saved={
                          row.id !== "" && (query.data ?? []).some((facet) => facet.id === row.id)
                        }
                        bindingKind={row.bindingKind}
                        bindingValue={row.bindingValue}
                        acts={row.acts}
                        seat={
                          seats.data?.facets.find((entry) => entry.facet.id === row.id)?.seat ??
                          null
                        }
                        onBinding={(bindingKind, bindingValue) =>
                          update(row.key, { bindingKind, bindingValue })
                        }
                        onActs={(acts) => update(row.key, { acts })}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {saveError ? (
          <p role="alert" className="text-small text-danger">
            {saveError}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Text variant="small">
            {rows.length >= MAX_THINKER_FACETS
              ? t("A thinker has at most {{count}} facets.", { count: MAX_THINKER_FACETS })
              : t("Facets save separately from the rest of this form.")}
          </Text>
          <Button
            size="sm"
            variant="primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate(rows.map(toInput))}
          >
            {t("Save facets")}
          </Button>
        </div>
      </Part>

      {/* THE HIVE: the facets thinking the same thing at once, voting, the authority deciding. */}
      <Part panel={panel} id="hive" className="pt-3">
        <HiveSettings
          kind={kind}
          id={id}
          policy={hive.data ?? null}
          facetCount={rows.length}
          hasAuthority={rows.some((row) => row.authority)}
        />
      </Part>

      {/* Where a thinker is TESTED: one real answer as itself — or the whole thought when the hive
          is on — or a blend-only dry run. The facets go in as typed, saved or not. */}
      <Part panel={panel} id="try" className="pt-3">
        <TryIt
          kind={kind}
          id={id}
          name={name ?? t("This thinker")}
          facets={rows.map(toInput)}
          hive={hive.data ?? null}
        />
      </Part>
    </div>
  );
}
